import { useContext, useId } from 'react'
import { MotionConfigContext, useReducedMotion } from 'framer-motion'
import { cn } from '../lib/utils'
import { emptyPatternColors, keyedMarks, positive, resolvePaint } from '../patterns/core'
import { usePatternTime } from '../patterns/use-pattern-time'
import type { PatternMark, PatternProps } from '../patterns/types'

export type { PatternProps } from '../patterns/types'

export function Pattern({ pattern, className, colors = emptyPatternColors, width = 320, height = 164, fit = 'contain', animated = false, time, label }: PatternProps) {
  positive(width, 'Pattern width')
  positive(height, 'Pattern height')
  const id = useId().replaceAll(':', '')
  const prefersReducedMotion = useReducedMotion()
  const motionConfig = useContext(MotionConfigContext)
  const reducedMotion = motionConfig.reducedMotion === 'always' || prefersReducedMotion !== false
  const seconds = usePatternTime(animated && !reducedMotion && !!pattern.frame, time)
  const layers = pattern.frame && (time !== undefined || (animated && !reducedMotion)) ? pattern.frame(seconds) : pattern.layers
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={cn('block w-full text-primary', className)} style={{ aspectRatio: width / height }} preserveAspectRatio={fit === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet'} aria-hidden={label ? undefined : true} role={label ? 'img' : undefined} aria-label={label} focusable="false" pointerEvents="none">
      {layers.map((layer, index) => {
        const key = `${id}-${index}`
        return (
          <g key={key} opacity={layer.opacity} transform={layer.transform}>
            <defs>
              {layer.tile && <pattern id={key} patternUnits="userSpaceOnUse" width={layer.tile.width} height={layer.tile.height} x={layer.tile.x} y={layer.tile.y} patternTransform={layer.tile.transform}>
                {keyedMarks(layer.marks).map(({ key: markId, mark }) => <Mark key={markId} mark={mark} colors={colors} />)}
              </pattern>}
              {layer.cutouts && <mask id={`${key}-mask`} maskUnits="userSpaceOnUse" x={0} y={0} width={width} height={height} style={{ maskType: 'luminance' }}>
                <rect width={width} height={height} fill="white" />
                {keyedMarks(layer.cutouts).map(({ key: markId, mark }) => <Mark key={markId} mark={{ ...mark, fill: mark.fill === 'none' ? 'none' : 'black', stroke: mark.stroke ? 'black' : undefined }} colors={{}} />)}
              </mask>}
              {layer.blur && <filter id={`${key}-blur`} x="-30%" y="-50%" width="160%" height="200%" colorInterpolationFilters="sRGB">
                <feGaussianBlur stdDeviation={layer.blur.join(' ')} edgeMode="duplicate" />
              </filter>}
            </defs>
            <g mask={layer.cutouts ? `url(#${key}-mask)` : undefined} filter={layer.blur ? `url(#${key}-blur)` : undefined}>
              {layer.tile ? <rect width={width} height={height} fill={`url(#${key})`} /> : keyedMarks(layer.marks).map(({ key: markId, mark }) => <Mark key={markId} mark={mark} colors={colors} />)}
            </g>
          </g>
        )
      })}
    </svg>
  )
}

function Mark({ mark, colors }: { mark: PatternMark; colors: Readonly<Record<string, string>> }) {
  const { kind, id: _id, ...props } = mark
  const paint = { fill: resolvePaint(mark.fill ?? 'currentColor', colors), stroke: resolvePaint(mark.stroke, colors) }
  switch (kind) {
    case 'rect': return <rect {...props} {...paint} />
    case 'circle': return <circle {...props} {...paint} />
    case 'path': return <path {...props} {...paint} />
    case 'image': return <image {...props} preserveAspectRatio="xMidYMid slice" />
  }
  return null
}
